import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Room, RoomEvent } from "livekit-client";
import { getGlobalAudioContext } from "../../utils/audioContext";
import { obs, ObsEvent, LatencyTracker } from "../../services/observability";

export const LIVEKIT_COMMAND_CHANNEL_TOPIC = "agent-control";
export const LIVEKIT_SERVER_RESPONSE_CHANNEL_TOPIC = "agent-response";

// Pure LiveKit subscriber for the avatar video/audio tracks.
// In CUSTOM/LITE mode the backend now holds the WebSocket to LiveAvatar's
// realtime endpoint and forwards ElevenLabs PCM directly as agent.speak
// events, so this component no longer opens a WebSocket or bridges audio
// chunks from Socket.IO. In FULL mode it still publishes text speak
// commands over the LiveKit `agent-control` data channel.
const HeygenStream = forwardRef(({ livekitUrl, accessToken, sessionId, onAvatarSpeakingChange, onRoomConnected, onRoomError }, ref) => {
    const videoRef = useRef(null);
    const roomRef = useRef(null);
    const audioRef = useRef(null);

    const connectTrackerRef = useRef(null);
    const speakCommandTimeRef = useRef(null);

    const onAvatarSpeakingChangeRef = useRef(onAvatarSpeakingChange);
    useEffect(() => {
        onAvatarSpeakingChangeRef.current = onAvatarSpeakingChange;
    }, [onAvatarSpeakingChange]);

    const onRoomConnectedRef = useRef(onRoomConnected);
    useEffect(() => {
        onRoomConnectedRef.current = onRoomConnected;
    }, [onRoomConnected]);

    const onRoomErrorRef = useRef(onRoomError);
    useEffect(() => {
        onRoomErrorRef.current = onRoomError;
    }, [onRoomError]);

    // ============== LiveKit command publishing (FULL mode) ==============

    const sendLiveKitCommand = async (commandEvent) => {
        const roomState = roomRef.current?.state;

        if (!roomRef.current || roomState === 'disconnected') {
            console.warn('[HeygenStream] Room not available for control event:', commandEvent?.event_type, 'Room state:', roomState);
            return;
        }

        if (roomState !== 'connected') {
            console.log('[HeygenStream] Waiting for room to connect, current state:', roomState);
            for (let i = 0; i < 50; i++) {
                await new Promise(resolve => setTimeout(resolve, 100));
                if (roomRef.current?.state === 'connected') {
                    break;
                }
            }
        }

        if (roomRef.current?.state !== 'connected') {
            console.error('[HeygenStream] Room still not connected after waiting, state:', roomRef.current?.state);
            return;
        }

        const data = new TextEncoder().encode(JSON.stringify(commandEvent));

        if (data.length > 65535) {
            console.error('[HeygenStream] Command too large:', data.length, 'bytes. Max is 65535 bytes');
            return;
        }

        try {
            await roomRef.current.localParticipant.publishData(data, {
                reliable: true,
                topic: LIVEKIT_COMMAND_CHANNEL_TOPIC,
            });
            console.log("[HeygenStream] [LK] Command sent:", commandEvent.event_type, data.length, "bytes");
        } catch (err) {
            console.error("[HeygenStream] [LK] Command failed", err);
        }
    };

    // FULL mode only: send text to the avatar via LiveKit data channel
    const sendSpeakCommand = async (text) => {
        speakCommandTimeRef.current = Date.now();

        await sendLiveKitCommand({
            event_type: "avatar.speak_text",
            text: text,
        });
    };

    const startRoomAudio = async () => {
        if (!roomRef.current || roomRef.current.canPlaybackAudio) {
            return;
        }
        try {
            await roomRef.current.startAudio();
        } catch (err) {
            console.error("[HeygenStream] Failed to start audio:", err);
        }
    };

    useImperativeHandle(ref, () => ({
        sendSpeakCommand,
        sendLiveKitCommand,
    }));

    useEffect(() => {
        const connectToRoom = async () => {
            connectTrackerRef.current = new LatencyTracker().start();

            const room = new Room({
                webAudioMix: {
                    audioContext: getGlobalAudioContext()
                }
            });
            roomRef.current = room;

            console.log("Room can playback audio: ", room.canPlaybackAudio);

            room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
                if (!room.canPlaybackAudio) {
                    startRoomAudio();
                }
            });

            room.on(RoomEvent.Disconnected, (reason) => {
                console.log("[HeygenStream] Room disconnected:", reason);

                const reasonMap = {
                    0: 'UNKNOWN',
                    1: 'CLIENT_INITIATED',
                    2: 'DUPLICATE_IDENTITY',
                    3: 'SERVER_SHUTDOWN',
                    4: 'PARTICIPANT_REMOVED',
                    5: 'ROOM_DELETED',
                    6: 'STATE_MISMATCH',
                    7: 'JOIN_FAILURE'
                };

                const isExpected = reason === 1;

                obs.emit(ObsEvent.LIVEKIT_ROOM_DISCONNECTED, {
                    session_id: sessionId,
                    room_id: room.name,
                    reason: reason,
                    reason_name: reasonMap[reason] || 'UNKNOWN',
                    status: isExpected ? 'info' : 'error',
                    mode: 'VIDEO'
                });

                if (!isExpected && typeof onRoomErrorRef.current === 'function') {
                    onRoomErrorRef.current(reasonMap[reason] || 'Room disconnected');
                }
            });

            room.on(RoomEvent.ConnectionStateChanged, (state) => {
                console.log("[HeygenStream] Connection state changed:", state);
            });

            room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                if (track.kind === "video") {
                    track.attach(videoRef.current);
                }
                if (track.kind === "audio") {
                    if (audioRef.current) {
                        track.attach(audioRef.current);
                        console.log("[HeygenStream] Audio track attached to audio element");
                    }
                }
            });

            room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
                const decoder = new TextDecoder();
                const strData = decoder.decode(payload);
                let jsonData;

                console.log("[HeygenStream] DataReceived - Topic:", topic, "Participant:", participant?.identity, "Data:", strData);

                try {
                    jsonData = JSON.parse(strData);

                    if (typeof jsonData === 'string') {
                        try {
                            jsonData = JSON.parse(jsonData);
                        } catch (e) {
                            // Not double-encoded
                        }
                    }

                    console.log("[HeygenStream] Parsed event_type:", jsonData?.event_type, "Full data:", jsonData);

                    if (jsonData?.event_type === "avatar.speak_started") {
                        console.log("[HeygenStream] Avatar started speaking");

                        if (speakCommandTimeRef.current) {
                            const speakLatencyMs = Date.now() - speakCommandTimeRef.current;
                            obs.emit(ObsEvent.HEYGEN_SPEAK_STARTED, {
                                session_id: sessionId,
                                speak_latency_ms: speakLatencyMs,
                                status: 'success'
                            });
                            speakCommandTimeRef.current = null;
                        }

                        if (typeof onAvatarSpeakingChangeRef.current === 'function') {
                            onAvatarSpeakingChangeRef.current(true);
                        }
                    } else if (jsonData?.event_type === "avatar.speak_ended") {
                        console.log("[HeygenStream] Avatar stopped speaking");

                        obs.emit(ObsEvent.HEYGEN_SPEAK_ENDED, {
                            session_id: sessionId,
                            status: 'success',
                            mode: 'VIDEO'
                        });

                        if (typeof onAvatarSpeakingChangeRef.current === 'function') {
                            onAvatarSpeakingChangeRef.current(false);
                        }
                    }
                } catch (e) {
                    console.warn("[HeygenStream] Non-JSON or error data:", strData, "Error:", e.message);
                }
            });

            try {
                await room.connect(livekitUrl, accessToken);

                const connectionMs = connectTrackerRef.current?.elapsed() || null;
                console.log("[HeygenStream] Connected to LiveKit successfully, room:", room.name);

                obs.emit(ObsEvent.LIVEKIT_ROOM_CONNECTED, {
                    session_id: sessionId,
                    room_id: room.name,
                    connection_ms: connectionMs,
                    room_state: room.state,
                    num_participants: room.numParticipants,
                    status: 'success',
                    mode: 'VIDEO'
                });

                if (!room.canPlaybackAudio) {
                    await startRoomAudio();
                }

                if (typeof onRoomConnectedRef.current === 'function') {
                    onRoomConnectedRef.current();
                }
            } catch (error) {
                console.error("LiveKit Connection Error:", error);

                obs.emit(ObsEvent.LIVEKIT_ROOM_DISCONNECTED, {
                    session_id: sessionId,
                    error_message: error?.message,
                    error_code: error?.code,
                    status: 'error',
                    mode: 'VIDEO'
                });

                if (typeof onRoomErrorRef.current === 'function') {
                    onRoomErrorRef.current(error?.message || 'LiveKit connection failed');
                }
            }
        };

        connectToRoom();

        return () => {
            if (roomRef.current) {
                roomRef.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [livekitUrl, accessToken]);

    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <audio ref={audioRef} autoPlay playsInline />
        </div>
    );
});

export default HeygenStream;
